/* eslint-disable jsx-a11y/click-events-have-key-events */
import './FilesList.scss';

import { Checkbox, MessageBar, MessageBarBody } from '@fluentui/react-components';
import {
  Add20Filled,
  ArrowDownloadRegular,
  ArrowSortRegular,
  ClipboardCheckmarkRegular,
  ArrowUploadRegular,
  DeleteRegular,
  TvArrowRightRegular,
  WindowNewRegular,
} from '@fluentui/react-icons';
import { FileItem, FolderWritePermissions, NewFileType } from '@mnspro-cloud/services';
import cn from 'classnames';
import { get } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CommunicationMessage, Illustrations, ThemeMode, useProviderTheme } from '../..';
import { Button } from '../../Button/Button';
import { ExcelIcon } from '../../Icons/ExcelIcon';
import { PowerPointIcon } from '../../Icons/PowerPointIcon';
import { WordIcon } from '../../Icons/WordIcon';
import { Loader } from '../../Loader';
import CustomSplitButton, {
  CustomSplitButtonMenuItem,
} from '../../CustomSplitButton/CustomSplitButton';
import { FolderIcon } from '../../Icons/FolderIcon';
import { FileNewFolderDialog } from '../FileNewFolderDialog';
import { filterFileListForAllowedExtensions } from '../Files.utils';
import { getDownloadMode, getSelectAllState, isFolder, isSelectable } from './FilesList.utils';
import { FilesListRow } from './FilesListRow';

export interface FilesListProps {
  files: FileItem[];
  filesError?: boolean;
  isLoading: boolean;
  onFolderClick: (folder: FileItem) => void;
  hasParent: boolean;
  maxSelectableItems: number | null;
  allowFolderSelection: boolean;
  onlyFolderSelection?: boolean;
  onNavigateToParentClick: () => void;
  onFileSelectionChange: (files: FileItem[]) => void;
  toggleSidebarAction?: JSX.Element;
  onOpenFileClick?: (file: FileItem) => void;
  onNewFileUpload?: (fileList: FileList) => void;
  onDeleteFilesClick?: (fileItems: FileItem[]) => void;
  onDownloadFileClick?: (fileItem: FileItem) => void;
  onDownloadZipClick?: (fileItems: FileItem[]) => void;
  onPreviewFileClick?: (fileItem: FileItem) => void;
  onNewFolderClick?: (name: string, writeAccess: FolderWritePermissions) => void;
  onNewFileClick?: (type: NewFileType) => void;
  onShareFileClick?: (file: FileItem) => void;
  onDownloadAsPdfClick?: (file: FileItem) => void;
  onMoveFileClick?: (files: FileItem[]) => void;
  onRenameFileClick?: (file: FileItem) => void;
  allowedFileExtensions?: string[];
  onCopyClick?: (files: FileItem[]) => void;
  onCopyTargetClick?: () => void;
  consentRequired?: boolean;
  onOpenInSharePoint?: () => void;
  isSchoolWideDrive?: boolean;
  /**
   * Hides the action toolbar entirely. Use when the list is embedded as a
   * pure picker (e.g. a copy/move target selector) where the file actions
   * (open, upload, move …) are irrelevant and the empty bar just adds clutter.
   */
  hideToolbar?: boolean;
  /**
   * Defines which write permissions should be preselected
   * when creating a new folder.
   */
  defaultValueWritePermissions?: FolderWritePermissions;
}

const canBeOpened = (selected: FileItem[]): boolean =>
  selected.length === 1 && get(selected, '0.webUrl', false) !== false;

/**
 * Renders a list of files / folder contents
 */
export const FilesList: React.FC<FilesListProps> = ({
  files,
  isLoading,
  filesError,
  onFolderClick,
  hasParent,
  onNavigateToParentClick,
  onFileSelectionChange,
  onNewFileUpload = null,
  maxSelectableItems = null,
  allowFolderSelection = false,
  onlyFolderSelection = false,
  onOpenFileClick = null,
  onDeleteFilesClick = null,
  onDownloadFileClick = null,
  onDownloadZipClick = null,
  onPreviewFileClick = null,
  onNewFolderClick = null,
  onNewFileClick = null,
  onShareFileClick = null,
  onDownloadAsPdfClick = null,
  onRenameFileClick = null,
  allowedFileExtensions = [],
  onMoveFileClick = null,
  onCopyClick = null,
  toggleSidebarAction,
  consentRequired,
  onOpenInSharePoint,
  isSchoolWideDrive = false,
  hideToolbar = false,
  defaultValueWritePermissions,
}) => {
  const { t } = useTranslation();
  const theme = useProviderTheme();
  const [selected, setSelected] = useState<FileItem[]>([]);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uploadInputReference = useRef<any>(null);

  const handleButtonUploadClick = () => uploadInputReference.current.click();

  const handleNewFileUpload = (fileList: FileList) => {
    if (onNewFileUpload !== null) {
      onNewFileUpload(fileList);
    }
  };

  const handleFileSelectionChange = (file: FileItem, next: boolean) => {
    setSelected((previous) =>
      next ? [...previous, file] : previous.filter((item) => item !== file),
    );
  };

  const handleButtonDeleteClick = () => {
    if (onDeleteFilesClick === null || selected.length === 0) {
      return;
    }
    onDeleteFilesClick(selected);
  };

  const downloadMode = getDownloadMode(
    selected,
    onDownloadFileClick !== null,
    onDownloadZipClick !== null,
  );

  const handleButtonDownloadClick = () => {
    if (downloadMode === 'file') {
      onDownloadFileClick?.(selected[0]);
    } else if (downloadMode === 'zip') {
      onDownloadZipClick?.(selected);
    }
  };

  const handleMoveFileClick = () => {
    if (onMoveFileClick === null) {
      return;
    }
    onMoveFileClick(selected);
  };

  const handleCopyClick = () => {
    if (onCopyClick === null) {
      return;
    }
    onCopyClick(selected);
  };

  const handleButtonNewFolderClick = () => {
    setShowNewFolderDialog(true);
  };

  const handleConfirmNewFolderClick = (folderName: string, writeAccess: FolderWritePermissions) => {
    setShowNewFolderDialog(false);

    if (onNewFolderClick === null) {
      return;
    }

    onNewFolderClick(folderName, writeAccess);
  };

  const handleCancelNewFolderClick = () => {
    setShowNewFolderDialog(false);
  };

  const handleOnOpenClick = () => {
    const file = get(selected, '0', false);

    if (file === false) {
      return;
    }

    handleOpenFile(file as FileItem);
  };

  const handleOpenFile = (file: FileItem) => {
    if (get(file, 'folder', false) !== false) {
      onFolderClick(file);
      return;
    }

    const webUrl = get(file, 'webUrl', false);

    if (webUrl) {
      window.open(webUrl, '_blank');
      return;
    }

    if (onOpenFileClick !== null) {
      onOpenFileClick(file);
      return;
    }
  };

  const handleOpenInSharePoint = () => {
    if (!onOpenInSharePoint) {
      return;
    }

    onOpenInSharePoint();
  };

  useEffect(() => {
    onFileSelectionChange(selected);
  }, [onFileSelectionChange, selected]);

  useEffect(() => {
    setSelected([]);
  }, [files]);

  const newFileMenu = useMemo(() => {
    const menu: {
      key: NewFileType;
      icon: JSX.Element;
      content: string;
    }[] = [];

    if (onNewFolderClick !== null) {
      menu.push({
        key: NewFileType.Folder,
        icon: <FolderIcon />,
        content: t('folder', { defaultValue: 'Ordner' }),
      });
    }

    if (onNewFileClick !== null) {
      menu.push(
        {
          key: NewFileType.Docx,
          icon: <WordIcon />,
          content: t('word_document', { defaultValue: 'Word-Dokument' }),
        },
        {
          key: NewFileType.Pptx,
          icon: <PowerPointIcon />,
          content: t('powerpoint_presentation', {
            defaultValue: 'Powerpoint-Präsentation',
          }),
        },
        {
          key: NewFileType.Xlsx,
          icon: <ExcelIcon />,
          content: t('excel_document', { defaultValue: 'Excel-Dokument' }),
        },
      );
    }

    return menu;
  }, [onNewFolderClick, onNewFileClick, t]);

  const handleNewFileMenuItemClick = (
    _event: React.SyntheticEvent,
    data: CustomSplitButtonMenuItem,
  ) => {
    if (onNewFileClick === null && onNewFolderClick === null) {
      return;
    }

    switch (data.key) {
      case NewFileType.Folder:
        if (onNewFolderClick) {
          handleButtonNewFolderClick();
        }
        break;

      default:
        if (onNewFileClick) {
          onNewFileClick(data.key as NewFileType);
        }
    }
  };

  const handleDeleteFileContextMenu =
    onDeleteFilesClick === null ? null : (fileItem: FileItem) => onDeleteFilesClick([fileItem]);

  const filteredFolderContents: FileItem[] =
    allowedFileExtensions.length === 0
      ? files
      : filterFileListForAllowedExtensions(files, allowedFileExtensions);

  const selectableFiles = filteredFolderContents.filter((file) =>
    isSelectable(file, allowFolderSelection, !onlyFolderSelection),
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectAllState = getSelectAllState(selectableFiles, selectedSet);
  const showSelectAll = selectableFiles.length > 0;

  const handleSelectAllChange = () => {
    setSelected(selectAllState === true ? [] : selectableFiles);
  };

  return (
    <div className="filesListComponent flex flex-col">
      {showNewFolderDialog && (
        <FileNewFolderDialog
          onConfirmClick={handleConfirmNewFolderClick}
          onCancelClick={handleCancelNewFolderClick}
          consentRequired={consentRequired}
          defaultValueWritePermissions={defaultValueWritePermissions}
        />
      )}
      <input
        type="file"
        multiple
        style={{ display: 'none' }}
        ref={uploadInputReference}
        onChange={(event) => handleNewFileUpload(event.target.files as FileList)}
      />
      {!hideToolbar && (
        <div className="filesListToolbar flex items-center">
          {toggleSidebarAction}
          {newFileMenu.length > 0 && (
            <CustomSplitButton
              data-cy="fileNewBtn"
              className="newBtn"
              primary
              disabled={isLoading || isSchoolWideDrive}
              menu={newFileMenu}
              aria-label={t('file_new_button_label', {
                defaultValue: 'Neu, Öffnet ein Flyout zum Erstellen von neuen Dateien',
              })}
              button={{
                content: t('new', { defaultValue: 'Neu' }),
                icon: <Add20Filled />,
              }}
              onMenuItemClick={handleNewFileMenuItemClick}
            />
          )}
          <Button
            className="filesListBtn btnOpenFile"
            aria-label={t('file_open_button_label', {
              defaultValue: 'Öffnen: Öffnet die ausgewählte Datei',
            })}
            disabled={!canBeOpened(selected)}
            appearance="transparent"
            icon={<WindowNewRegular />}
            onClick={handleOnOpenClick}
          >
            {t('open', { defaultValue: 'Öffnen' })}
          </Button>
          {onOpenInSharePoint && (
            <Button
              className="filesListBtn btnOpenInSharePoint"
              appearance="transparent"
              icon={<WindowNewRegular />}
              disabled={isLoading || isSchoolWideDrive}
              onClick={handleOpenInSharePoint}
            >
              {t('open_in_sharepoint', {
                defaultValue: 'In Sharepoint anzeigen',
              })}
            </Button>
          )}
          {onNewFileUpload && (
            <Button
              className="filesListBtn"
              aria-label={t('file_upload_button_label', {
                defaultValue: 'Hochladen, Öffnet den Datei-Explorer zum Hochladen von Dateien',
              })}
              appearance="transparent"
              icon={<ArrowUploadRegular />}
              disabled={isLoading || isSchoolWideDrive}
              onClick={handleButtonUploadClick}
            >
              {t('upload', { defaultValue: 'Hochladen' })}
            </Button>
          )}
          {(onDownloadFileClick || onDownloadZipClick) && (
            <Button
              className="filesListBtn btnDownloadFile"
              aria-label={
                downloadMode === 'zip'
                  ? t('files:fileslist:download_zip_button_label', {
                      defaultValue: 'Herunterladen: Lädt die Auswahl als ZIP-Archiv herunter',
                    })
                  : t('file_download_button_label', {
                      defaultValue: 'Herunterladen: Lädt die ausgewählte Datei herunter',
                    })
              }
              appearance="transparent"
              icon={<ArrowDownloadRegular />}
              onClick={handleButtonDownloadClick}
              disabled={downloadMode === null}
            >
              {t('download', { defaultValue: 'Herunterladen' })}
            </Button>
          )}
          {onShareFileClick !== null && (
            <Button
              className="filesListBtn btnShareFile"
              aria-label={t('share_file_label', {
                defaultValue: 'Freigeben: Öffnet Popup zum Freigeben der Datei',
              })}
              appearance="transparent"
              icon={<TvArrowRightRegular />}
              onClick={() => onShareFileClick(selected[0])}
              disabled={selected.length !== 1 || isFolder(selected[0])}
            >
              {t('share_link_short', { defaultValue: 'Freigeben' })}
            </Button>
          )}
          {onMoveFileClick && (
            <Button
              className="filesListBtn btnMoveFile"
              aria-label={t('file_move_button_label', {
                defaultValue: 'Verschieben: Öffnet ein Popup zum Auswählen des Zielordners',
              })}
              appearance="transparent"
              data-cy="fileMoveButton"
              icon={<ArrowSortRegular />}
              disabled={selected.length === 0 || isSchoolWideDrive}
              onClick={handleMoveFileClick}
            >
              {t('move', { defaultValue: 'Verschieben' })}
            </Button>
          )}

          {onCopyClick && (
            <Button
              aria-label={t('copy_file_label', {
                defaultValue: 'Kopieren: Öffnet Popup zum Auswählen des Zielordners für die Kopie',
              })}
              className="filesListBtn btnCopyFile"
              appearance="transparent"
              data-cy="fileCopyButton"
              icon={<ClipboardCheckmarkRegular />}
              disabled={selected.length === 0 || isSchoolWideDrive}
              onClick={handleCopyClick}
            >
              {t('copy', { defaultValue: 'Kopieren' })}
            </Button>
          )}
          {onDeleteFilesClick && (
            <Button
              aria-label={t('delete_file_label', {
                defaultValue: 'Löschen: Löscht ausgewählte Dateien',
              })}
              className="filesListBtn btnDeleteFile"
              appearance="transparent"
              data-cy="fileDeleteButton"
              icon={<DeleteRegular />}
              disabled={selected.length === 0 || isSchoolWideDrive}
              onClick={handleButtonDeleteClick}
            >
              {t('delete', { defaultValue: 'Löschen' })}
            </Button>
          )}
        </div>
      )}
      {maxSelectableItems !== null && selected.length > maxSelectableItems && (
        <MessageBar intent="error">
          <MessageBarBody>
            {t('files_list_max_selectable_items_exceeded', {
              defaultValue: 'Es können maximal nur {{maxItemsCount}} Dateien ausgewählt werden',
              maxItemsCount: maxSelectableItems,
            })}
          </MessageBarBody>
        </MessageBar>
      )}
      {isLoading && !filesError && (
        <div className="filesListLoadingOverlay flex items-center justify-center">
          <Loader />
        </div>
      )}

      {filesError && (
        <CommunicationMessage
          illustration={Illustrations.Error}
          headline={t('file_error', { defaultValue: 'Fehler' })}
          text={t('file_error_text', {
            defaultValue: 'Beim Laden der Dateien ist ein Fehler aufgetreten',
          })}
          useDarkIllustration={theme.mode === ThemeMode.Dark}
        />
      )}

      <div className="filesListWrapper">
        {!isLoading && !hasParent && files.length === 0 ? (
          <CommunicationMessage
            illustration={Illustrations.Empty}
            headline={t('file_drive_empty_headline', { defaultValue: 'Keine Dateien vorhanden' })}
            text={t('file_drive_empty_text', {
              defaultValue: 'Der von Ihnen ausgewählte Order ist leer',
            })}
            useDarkIllustration={theme.mode === ThemeMode.Dark}
          />
        ) : (
          <ul className="filesList">
            {(files.length > 0 || hasParent) && (
              <li
                className={cn('filesListRow', 'filesListRowHeader', {
                  filesListRowHeaderSelectable: showSelectAll,
                })}
              >
                <span className="filesListColCheckbox">
                  {showSelectAll && (
                    <Checkbox
                      checked={selectAllState}
                      onChange={handleSelectAllChange}
                      aria-label={t('files:fileslist:select_all_items_label', {
                        defaultValue: 'Alle Einträge auswählen',
                      })}
                    />
                  )}
                </span>
                <span className="filesListColIcon"></span>
                <span className="filesListColFilename">
                  {t('file_name', { defaultValue: 'Dateiname' })}
                </span>
                <span className="filesListColMenuWrapper"></span>
                <span className="filesListColFilesize">
                  {t('file_size', { defaultValue: 'Größe' })}
                </span>
                <span className="filesListColLastModifiedBy">
                  {t('file_modified_by', { defaultValue: 'Geändert von' })}
                </span>
                <span className="filesListColLastModifiedDate">
                  {t('file_modified_date', { defaultValue: 'Geändert am' })}
                </span>
              </li>
            )}
            {hasParent && (
              <li className="filesListRow filesListRowNavToParent">
                <button
                  className="filesListRowNavToParentButton"
                  aria-label={t('file_parent_folder_label', {
                    defaultValue: 'Wechselt in den überliegenden Ordner',
                  })}
                  onClick={onNavigateToParentClick}
                >
                  <span className="filesListColIcon">
                    <FolderIcon style={{ width: '16px' }} />
                  </span>
                  <span className="filesListColFilename">...</span>
                </button>
              </li>
            )}
            {filteredFolderContents.map((file) => (
              <FilesListRow
                key={file.id}
                file={file}
                onFolderClick={onFolderClick}
                isSelected={selectedSet.has(file)}
                onSelectionChange={(next) => {
                  if (onlyFolderSelection && !file.folder) {
                    return;
                  }
                  handleFileSelectionChange(file, next);
                }}
                allowFileSelection={!onlyFolderSelection}
                allowFolderSelection={allowFolderSelection}
                onShareFileClick={onShareFileClick}
                onDownloadAsPdfClick={onDownloadAsPdfClick}
                onOpenFileClick={handleOpenFile}
                onDownloadFileClick={onDownloadFileClick}
                onDownloadZipClick={onDownloadZipClick}
                onPreviewFileClick={onPreviewFileClick}
                onRenameFileClick={onRenameFileClick}
                onDeleteFileClick={handleDeleteFileContextMenu}
                // Move file is currently broken: The source file gets deleted, but
                // target file won't get created, when files are moved between
                // drives
                onMoveFileClick={onMoveFileClick}
                onCopyFileClick={onCopyClick}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
